import React, {Component, ErrorInfo, ReactNode} from 'react';

import {AlertTriangle, RefreshCw, Home} from 'lucide-react';

import {
    ErrorBoundaryProps,
    ErrorBoundaryState,
    ErrorFallbackProps,
} from '../declarations';

/* -------------------- Error Fallback UI -------------------- */
const ErrorFallback: React.FC<ErrorFallbackProps> = ({error, onReload}) => (
    <div className='min-h-screen flex items-center justify-center bg-[rgb(var(--bg-primary))] p-4'>
        <div className='card max-w-md w-full p-8 text-center'>
            {/* Icon */}
            <AlertTriangle
                className='w-16 h-16 text-red-400 mx-auto mb-4'
                aria-hidden='true'
            />

            {/* Title */}
            <h1 className='text-2xl font-bold mb-2 text-[rgb(var(--text-primary))]'>
                Something went wrong
            </h1>

            {/* Description */}
            <p className='text-[rgb(var(--text-secondary))] mb-6'>
                Sorry, an unexpected error occurred.
            </p>

            {/* Error Details */}
            {error && (
                <details className='mb-6 text-left'>
                    <summary className='cursor-pointer text-sm font-medium text-[rgb(var(--text-secondary))] mb-2'>
                        Show error details
                    </summary>

                    <div className='mt-2 p-3 bg-[rgb(var(--bg-secondary))] rounded-lg text-xs font-mono overflow-auto max-h-40'>
                        <p className='text-red-400 mb-1'>{error.toString()}</p>
                    </div>
                </details>
            )}

            {/* Actions */}
            <div className='flex flex-col sm:flex-row gap-3'>
                <button
                    onClick={onReload}
                    className='btn-primary flex items-center justify-center gap-2 flex-1'
                >
                    <RefreshCw size={18} aria-hidden='true' />
                    Reload page
                </button>

                <a
                    href='/'
                    className='btn-secondary flex items-center justify-center gap-2 flex-1'
                >
                    <Home size={18} aria-hidden='true' />
                    Go to homepage
                </a>
            </div>

            {/* Support */}
            <p className='text-sm text-[rgb(var(--text-secondary))] mt-6'>
                If the problem persists,{' '}
                <a
                    href={`mailto:mail@es2be.de`}
                    className='text-[rgb(var(--accent-primary))] hover:underline'
                >
                    contact us
                </a>
            </p>
        </div>
    </div>
);

/* -------------------- Error Boundary -------------------- */
class ErrorBoundary extends Component<ErrorBoundaryProps, ErrorBoundaryState> {
    state: ErrorBoundaryState = {
        hasError: false,
        error: null,
    };

    static getDerivedStateFromError(error: Error): ErrorBoundaryState {
        return {hasError: true, error};
    }

    componentDidCatch(error: Error, errorInfo: ErrorInfo): void {
        console.error('ErrorBoundary caught an error:', error, errorInfo);
    }

    private handleReload = (): void => {
        window.location.reload();
    };

    render(): ReactNode {
        const {hasError, error} = this.state;
        const {children, fallback} = this.props;

        if (!hasError) return children;
        if (fallback) return fallback;

        return <ErrorFallback error={error} onReload={this.handleReload} />;
    }
}

export default ErrorBoundary;
